import React, { useState } from "react"
import { useTranslation } from "react-i18next"
import BoxButton from "../../Radiate/NewBoxButton/NewBoxButton"
import * as Styles from "./styles"

const HOOK_SKELETON = `export default async function ({ messageEvent, thread, channel, agent }) {
  // inspect or mutate messageEvent here
  // return undefined  -> send unchanged
  // return { ...messageEvent } -> replace the outbound message
  // throw new Error("...") -> block the send (agent sees the message)
  return messageEvent
}`

const ARGS_SNIPPET = `// messageEvent — the outbound message (mutate / replace / throw to block)
{ type: "TEXT", data: { text: "hello world" } }   // shape varies by type

// thread
{ threadId, wChannelId, wMemberId, folder, title, externalId }

// channel
{ wChannelId, wAppId, type, name, meta }

// agent — the sender
{ wUserId, role }`

const SandboxHelp = () => {
  const { t } = useTranslation(["settings", "common"])
  const [isOpen, setIsOpen] = useState(false)

  return (
    <Styles.HelpPanel>
      <Styles.SectionHeader
        onClick={() => setIsOpen((v) => !v)}
        style={{ padding: 0 }}
      >
        <Styles.SectionTitleText style={{ fontSize: 14 }}>
          {t("outbound_hooks.sandbox_help.title", "Sandbox capabilities")}
        </Styles.SectionTitleText>
        <BoxButton icon={isOpen ? "chevron-down" : "chevron-right"} borderless />
      </Styles.SectionHeader>

      {isOpen && (
        <div>
          <p>
            {t(
              "outbound_hooks.sandbox_help.intro",
              "A hook is an async function that runs before an outbound message is sent. It may inspect or mutate the message, or throw to block it."
            )}
          </p>
          <Styles.CodeSnippet>{HOOK_SKELETON}</Styles.CodeSnippet>

          <strong>{t("outbound_hooks.sandbox_help.arguments", "Arguments")}</strong>
          <p>
            {t(
              "outbound_hooks.sandbox_help.arguments_note",
              "Each argument is a plain JSON snapshot (not a live document). Only messageEvent may be mutated; in a multi-hook chain the mutated messageEvent is passed to the next hook."
            )}
          </p>
          <Styles.CodeSnippet>{ARGS_SNIPPET}</Styles.CodeSnippet>

          <strong>{t("outbound_hooks.sandbox_help.available", "Available")}</strong>
          <ul>
            <li>
              {t(
                "outbound_hooks.sandbox_help.ecmascript",
                "Full modern ECMAScript: async/await, classes, optional chaining (?.), nullish coalescing (??), destructuring, template literals, BigInt, globalThis, and the standard built-ins (JSON, Math, Date, Map/Set, Promise)."
              )}
            </li>
            <li>
              {t("outbound_hooks.sandbox_help.globals", "Globals:")} <code>fetch</code>{" "}
              {t("outbound_hooks.sandbox_help.fetch_note", "(allowlisted hosts only)")},{" "}
              <code>console</code>, <code>btoa</code>/<code>atob</code>,{" "}
              <code>setTimeout</code>/<code>setInterval</code>/<code>clearTimeout</code>/
              <code>clearInterval</code>, <code>AbortController</code>.
            </li>
            <li>
              {t("outbound_hooks.sandbox_help.modules", "Modules:")}{" "}
              <code>import _ from "lodash"</code>, <code>import CryptoJS from "crypto-js"</code>.
            </li>
          </ul>
        </div>
      )}
    </Styles.HelpPanel>
  )
}

export default SandboxHelp
